import { ComboBox, DirectionalHint, FocusTrapZone, IconButton, IDropdownOption, Shimmer, ShimmerElementType, TextField, TooltipHost } from "@fluentui/react"
import React from "react";
import { useEffect, useRef, useState } from "react";
import styles from './PodDetailItem.module.scss';

interface Props {
  type: "text" | "textarea" | "select" | "multiselect" | "readonly",
  title: string,
  value: string | string[] | null,
  options?: IDropdownOption[],
  hasUnsavedChanges?: boolean,
  className?: string,
  emptyValueClassName?: string,
  disabled?: boolean,
  disabledTooltip?: string,
  resetDisabled?: boolean,
  resetDisabledTooltip?: string,
  onRender?: (value: any) => React.ReactNode,
  onCommit?: (value: any) => void,
  onReset?: () => string | string[],
}

interface EditorProps extends Pick<Props, 'type' | 'value' | 'options'> {
  onChange: (value: any) => void,
  enterHandle?: () => void,
  escapeHandle?: () => void,
}

const PodDetailEditor = ({type, value, options, onChange, enterHandle, escapeHandle}: EditorProps) => {
  switch (type) {
    case "text":
      return (
        <TextField
          autoFocus
          value={value as string}
          onChange={(_, val) => onChange(val)}
          onKeyUp={(e) => {
            if (e.key === 'Enter' && enterHandle) {
              enterHandle();
            } else if (e.key === 'Escape' && escapeHandle) {
              escapeHandle();
            }
          }}
        />
      );
    case "textarea":
      return (
        <TextField
          autoFocus
          multiline
          autoAdjustHeight
          value={value as string}
          onChange={(_, val) => onChange(val)}
        />
      );
    case "select":
      return (
        <ComboBox
          allowFreeform={true}
          selectedKey={value}
          onChange={(_, option) => onChange(option?.key)}
          options={options ?? []}
        />
      );
    case "multiselect":
      return (
        <ComboBox
          multiSelect
          allowFreeform={true}
          selectedKey={value}
          onChange={(_, option) => {
            if (!option) return;
            const changedKey = option.key as string;
            if (option.selected) {
              if (value && value.includes(changedKey)) return;
              onChange([...(value as string[]), changedKey]);
            } else {
              onChange((value as string[]).filter(key => key !== changedKey));
            }
          }}
          options={options ?? []}
        />
      );
    default:
      return null;
  }
}

const PodDetailItem = ({type, title, value, options, hasUnsavedChanges, className, emptyValueClassName, disabled, disabledTooltip, resetDisabled, resetDisabledTooltip, onRender, onCommit, onReset}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [internalValue, setInternalValue] = useState<any>(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      setIsEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const valueStringified = type === 'multiselect'
    ? internalValue.join(", ")
    : internalValue;

  const defaultRender = internalValue === null
    ? null
    : internalValue.length > 0
      ? valueStringified
      : <em>No {title.toLowerCase()}</em>;

  return (
    <div className={`${styles.detailItem}${className ? ` ${className}` : ''}`}>
      <FocusTrapZone isClickableOutsideFocusTrap forceFocusInsideTrap={false} disabled={!isEditing}>
        <div className={styles.detailItemInner}>
          <div className={styles.itemHeader}>
            <strong aria-label={`${title}${hasUnsavedChanges ? '; has unsaved changes': ''}`}>
              {title}
              {hasUnsavedChanges && (
                <div className={styles.unsavedWrapper}>
                  <TooltipHost
                    key="detailUnsaved"
                    content="Unsaved"
                  >
                    <span className={styles.unsavedIcon}>&bull;</span>
                  </TooltipHost>
                </div>
              )}
            </strong>
            <div className={`${styles.itemActions} ${type === 'readonly' && styles.actionsHidden}`}>
              {isEditing ? (
                <>
                  <TooltipHost
                    key='detailEditCancel'
                    content={(resetDisabled && resetDisabledTooltip) || 'Reset'}
                    directionalHint={DirectionalHint.topRightEdge}
                    hostClassName={styles.tooltipHost}
                  >
                    <IconButton
                      iconProps={{ iconName: 'Cancel' }}
                      disabled={resetDisabled}
                      ariaLabel="Reset"
                      onClick={() => {
                        setIsEditing(false);
                        if (onReset) {
                          const valueOverride = onReset();
                          setInternalValue(valueOverride);
                        }
                      }}
                    />
                  </TooltipHost>
                  <TooltipHost
                    key='detailEditCommit'
                    content='Confirm'
                    hostClassName={styles.tooltipHost}
                  >
                    <IconButton
                      iconProps={{ iconName: 'CheckMark' }}
                      ariaLabel="Confirm"
                      onClick={() => {
                        setIsEditing(false);
                        if (onCommit) onCommit(internalValue);
                      }
                    }/>
                  </TooltipHost>
                </>
              ) : (
                <TooltipHost
                  key='detailEdit'
                  content={disabled ? disabledTooltip ?? 'Can’t edit' : 'Edit'}
                  hostClassName={styles.tooltipHost}
                >
                  <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    size={12}
                    disabled={!!disabled}
                    ariaLabel={disabled && disabledTooltip ? disabledTooltip : 'Edit'}
                    onClick={() => {
                      if (type !== 'readonly') {
                        setIsEditing(true);
                      }
                    }
                  }/>
                </TooltipHost>
              )}
            </div>
          </div>
          { isEditing && type !== 'readonly' ? (
            <PodDetailEditor
              type={type}
              value={internalValue}
              options={options}
              onChange={setInternalValue}
              enterHandle={() => {
                setIsEditing(false);
                if (onCommit && !disabled) onCommit(internalValue);
              }}
              escapeHandle={() => {
                setIsEditing(false);
                if (onReset && !resetDisabled) {
                  const valueOverride = onReset();
                  setInternalValue(valueOverride);
                }
              }}
            />
          ) : (
            <div className={`${styles.valueRenderArea} ${internalValue === null || internalValue.length === 0 ? emptyValueClassName : ''}`}>
              {onRender ? onRender(internalValue) : defaultRender}
            </div>
          ) }
        </div>
      </FocusTrapZone>
    </div>
  )
}

export const PodDetailItemShimmer = ({title, noEdit}: {title: string, noEdit?: boolean}) => {
  const valueWidth = useRef(325-15*Math.ceil(Math.random()*2));

  return (
    <div className={`${styles.detailItem} ${styles.shimmerDetailItem}`}>
      <div className={styles.itemHeader}>
        <strong>
          {title}
        </strong>
        <div className={styles.itemActions}>
          <Shimmer
            width={32}
            className={`${styles.shimmerButton} ${!!noEdit && styles.hidden}`}
            shimmerElements={[
              { type: ShimmerElementType.line, width: 32, height: 32 },
            ]}
          />
        </div>
      </div>
      <Shimmer
        width="100%"
        shimmerElements={[
          { type: ShimmerElementType.line, width: valueWidth.current, height: 18 },
          { type: ShimmerElementType.gap, width: '100%' }
        ]}
      />
    </div>
  );
}

export default PodDetailItem;